// node_modules
import {InputMask} from 'primereact/inputmask'
import React, { useContext, useEffect, useState } from "react";
import { useForm, Controller, get } from "react-hook-form";
import { useDispatch } from "react-redux";
import {useMobile} from '../../../hooks/useMobile'

// redux
import {
  useLazyGetDiningOptionListQuery,
  useCreateDiningOptionMutation,
  useUpdateDiningOptionMutation,
  useDeleteDiningOptionMutation,
  useSaveDiningOptionsOrderMutation,
} from "../../../redux/services/diningOption";
import { useGetStationListQuery } from "../../../redux/services/foodStation";
import { setLoading } from "../../../redux/slices/app.slice";

// primereact components
import { Button } from "primereact/button";
import { OrderList } from "primereact/orderlist";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { SelectButton } from "primereact/selectbutton";
import { Dropdown } from "primereact/dropdown";

// context
import { ToastContext } from "../../../App";

// utils
import getSubdomain from "../../../utils/getSubdomain";

// styles
import * as styles from "./DiningOptions.module.css";

const Scheduling = () => {
  const dispatch = useDispatch();
  const {isMobile} = useMobile(1142);

  const [
    getDiningOptions,
    {
      data: options,
      isSuccess: isGetDiningOptionSuccess,
      isError: isGetDiningOptionsError,
    },
  ] = useLazyGetDiningOptionListQuery();
  const [
    createDiningOption,
    {
      isSuccess: isCreateDiningOptionSuccess,
      isError: isCreateDiningOptionError,
      error: createDiningOptionError,
    },
  ] = useCreateDiningOptionMutation();
  const [
    updateDiningOption,
    {
      isSuccess: isUpdateDiningOptionSuccess,
      isError: isUpdateDiningOptionError,
      error: updateDiningOptionError,
    },
  ] = useUpdateDiningOptionMutation();
  const [
    deleteDiningOption,
    { isSuccess: isDeleteDiningOptionSuccess, isError: isDeleteDiningOptionError, error: deleteDiningOptionError },
  ] = useDeleteDiningOptionMutation();
  const [
    saveDiningOptionOrder,
    {
      isLoading: isSaveDiningOptionOrderLoading,
      isError: isSaveDiningOptionOrderError,
      error: saveDiningOptionOrderError,
    },
  ] = useSaveDiningOptionsOrderMutation();
  const {
    data: stations,
    isSuccess: isGetStationsSuccess,
    isError: isGetStationsError,
  } = useGetStationListQuery(getSubdomain());

  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [mode, setMode] = useState("create");
  const [id, setId] = useState(null);
  const [name, setName] = useState(null);
  const [selectedStation, setSelectedStation] = useState(null);
  const showToast = useContext(ToastContext);

  const { control, handleSubmit, setValue } = useForm({
    defaultValues: {
      title: "",
    },
  });

  useEffect(() => {
    if (isGetStationsSuccess || isGetStationsError) {
      dispatch(setLoading(false));
    }
  }, [isGetStationsSuccess, isGetStationsError]);

  useEffect(() => {
    if (selectedStation) {
      getDiningOptions({
        id: selectedStation.id,
        subDomain: getSubdomain(),
      });
    }
  }, [selectedStation]);
  
  useEffect(() => {
    if (isCreateDiningOptionSuccess) {
      showToast('success', 'Dining option', 'Dining option created!')
      setShowModal(false);
    }
  }, [isCreateDiningOptionSuccess])
  
  useEffect(() => {
    if (isUpdateDiningOptionSuccess) {
      showToast('success', 'Dining option', 'Dining option updated!')
      setShowModal(false);
    }
  }, [isUpdateDiningOptionSuccess])
  
  useEffect(() => {
    if (isCreateDiningOptionError)
      showToast(
        "error",
        createDiningOptionError?.status,
        createDiningOptionError?.data?.message ||
        (createDiningOptionError?.data?.errors && "Incorrect data")
      );
  }, [isCreateDiningOptionError])
  
  useEffect(() => {
    if (isGetDiningOptionsError)
      showToast("error", "Dining option", "Unable to load dining options");
  }, [isGetDiningOptionsError])
  
  useEffect(() => {
    if (isGetStationsError)
      showToast("error", "Station", "Unable to load stations");
  }, [isGetStationsError])
  
  useEffect(() => {
    if (isUpdateDiningOptionError)
      showToast(
        "error",
        updateDiningOptionError?.status,
        updateDiningOptionError?.data?.message ||
        (updateDiningOptionError?.data?.errors && "Incorrect data")
      );
  }, [isUpdateDiningOptionError])
  
  useEffect(() => {
    if (isSaveDiningOptionOrderError)
      showToast(
        "error",
        saveDiningOptionOrderError?.status,
        saveDiningOptionOrderError?.data?.message ||
        (saveDiningOptionOrderError?.data?.errors && "Incorrect data")
      );
  }, [isSaveDiningOptionOrderError])
  
  useEffect(() => {
    if(isDeleteDiningOptionSuccess)
      setShowDeleteModal(false)
  }, [isDeleteDiningOptionSuccess])
  
  useEffect(() => {
    if (isDeleteDiningOptionError)
      showToast(
        "error",
        deleteDiningOptionError?.status,
        deleteDiningOptionError?.data?.message
      );
  }, [
    isDeleteDiningOptionError
  ])

  const openModal = (mode, rowData) => (e) => {
    if (mode === "create") {
      setId(null);
      setValue("title", "");
      setValue("active", "Active");
    } else {
      setId(rowData.id);
      setValue("title", rowData.name);
      setValue("active", rowData.active ? "Active" : "Inactive");
    }
    setShowModal(true);
    setMode(mode);
  };
  
  const openDeleteModal = (rowData) => () => {
    setId(rowData.id);
    setName(rowData.name);
    setShowDeleteModal(true);
  }
  
  const onDelete = () => {
    deleteDiningOption({
      id: id,
      stationId: selectedStation.id,
      subdomain: getSubdomain(),
    })
  }
  
  const onCreate = (data) => {
    const payload = { name: data.title, station_id: selectedStation.id };
    if (data.active === "Active") {
      payload.active = true;
    } else {
      payload.active = false;
    }
    createDiningOption({ subdomain: getSubdomain(), data: payload });
  };

  const onSaveEdit = (data) => {
    const payload = { name: data.title };
    if (data.active === "Active") {
      payload.active = true;
    } else {
      payload.active = false;
    }
    updateDiningOption({
      id,
      stationId: selectedStation.id,
      subdomain: getSubdomain(),
      data: payload,
    });
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (mode === "create") handleSubmit(onCreate)();
      else handleSubmit(onSaveEdit)();
    }
  };
  
  const handleArrowUpClick = (item) => {
    let order = item.order - 1;
    const newList = options.map((opt)=>{
      if(opt.id === item.id)
        return { id: opt.id, order };
      if(opt.id !== item.id && order === opt.order)
        return { id: opt.id, order: order + 1 }
      return { id: opt.id, order: opt.order }
    })
    
    saveDiningOptionOrder({
      payload: newList,
      subDomain: getSubdomain(),
      stationId: selectedStation.id,
    });
  }
  
  const handleArrowDownClick = (item) => {
    let order = item.order + 1;
    const newList = options.map((opt)=>{
      if(opt.id === item.id)
        return { id: opt.id, order };
      if(opt.id !== item.id && order === opt.order)
        return { id: opt.id, order: order - 1 }
      return { id: opt.id, order: opt.order }
    })
    
    saveDiningOptionOrder({
      payload: newList,
      subDomain: getSubdomain(),
      stationId: selectedStation.id,
    });
  }
  
  const itemTemplate = (item) => {
    return (
      <div className={styles.diningOptionItem}>
        <div className={styles.column1}>
          <i className="pi pi-align-justify"></i>
        </div>
        <div style={{ width: "100%" }}>{item.name}</div>
        <div style={{ display: "flex", justifyContent: "center", flexShrink: 0, width: "160px", gap: '2px' }}>
          <Button icon="pi pi-arrow-up" className={styles.arrowBtn} disabled={item.order === 1} onMouseDown={()=>handleArrowUpClick(item)}/>
          <Button icon="pi pi-arrow-down" className={styles.arrowBtn} disabled={item.order === options[options.length - 1].order} onMouseDown={()=>handleArrowDownClick(item)}/>
        </div>
        <div
          style={{ display: "flex", justifyContent: "center", flexShrink: 0, width: "260px" }}
        >
          {item.active ? (
            <Button
              icon="pi pi-eye"
              className={`p-button-text ${styles.activeBtn}`}
              onMouseUp={() => {
                updateDiningOption({
                  id: item.id,
                  stationId: selectedStation.id,
                  subdomain: getSubdomain(),
                  data: { active: false },
                });
              }}
            />
          ) : (
            <Button
              icon="pi pi-eye-slash"
              className={`p-button-text ${styles.activeBtn}`}
              onMouseUp={() => {
                updateDiningOption({
                  id: item.id,
                  stationId: selectedStation.id,
                  subdomain: getSubdomain(),
                  data: { active: true },
                });
              }}
            />
          )}
        </div>
        <div style={{ width: "207px", textAlign: "center", flexShrink: 0 }}>
          <div className={styles.cellSoundWrapper}>
            <Button
              icon="pi pi-pencil"
              className={`p-button-text ${styles.soundBtn}`}
              onMouseDown={openModal("edit", item)}
            />
            <Button
              icon="pi pi-trash"
              className={`p-button-text ${styles.deleteBtn}`}
              onMouseDown={openDeleteModal(item)}
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.pageHeader}>
        <h2>Dining Options</h2>
        <Dropdown
          value={selectedStation}
          onChange={(e) => setSelectedStation(e.value)}
          options={stations?.toSorted((a, b) => a.name.localeCompare(b.name))}
          optionLabel="name"
          placeholder="Station"
          className={`${styles.dropdown}`}
        />
      </div>
      {isGetStationsSuccess && (
        <div style={{ marginBottom: "10px" }}>
          {selectedStation && (
            <Button className={styles.createBtn} onClick={openModal("create")}>
              <i className="pi pi-plus"></i> <span>ADD DINING OPTION</span>
            </Button>
          )}
        </div>
      )}

      {selectedStation && isGetDiningOptionSuccess ? (
        <>
          {options?.length ? (
            <>
              {isMobile ? (
                <>
                  <div className={styles.actionsText}>Reorder options using arrows</div>
                  <div className={styles.cardList}>
                    {options.map((option) => (
                      <div key={option.id} className={styles.cardItem}>
                        <div className={styles.cardInfo}>
                          <div className={styles.name}>{option.name}</div>
                        </div>
                        <div className={styles.actions}>
                          <button className={styles.cardArrowBtn} disabled={option.order === 1}  onClick={()=>handleArrowUpClick(option)}>
                            <i className="pi pi-arrow-up" />
                          </button>
                          <button className={styles.cardArrowBtn} disabled={option.order === options[options.length - 1].order}  onClick={()=>handleArrowDownClick(option)}>
                            <i className="pi pi-arrow-down" />
                          </button>
                        </div>
                        <div className={styles.actions}>
                          {option.active ? (
                            <button
                              className={styles.cardEditBtn}
                              onClick={() => {
                                updateDiningOption({
                                  id: option.id,
                                  stationId: selectedStation.id,
                                  subdomain: getSubdomain(),
                                  data: { active: false },
                                });
                              }}
                            >
                              <i className="pi pi-eye" />
                            </button>
                          ) : (
                            <button
                              className={styles.cardEditBtn}
                              onClick={() => {
                                updateDiningOption({
                                  id: option.id,
                                  stationId: selectedStation.id,
                                  subdomain: getSubdomain(),
                                  data: { active: true },
                                });
                              }}
                            >
                              <i className="pi pi-eye-slash" />
                            </button>
                          )}
                          <button className={styles.cardEditBtn} onClick={openModal("edit", option)}>
                            <i className="pi pi-pencil" />
                          </button>
                          <button className={styles.cardDeleteBtn} onClick={openDeleteModal(option)}>
                            <i className="pi pi-trash" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <>
                  <div>Drag options to reorder</div>
                  <OrderList
                    dataKey="id"
                    value={options}
                    onChange={(e) => {
                      const newList = e.value.map((item, index) => {
                        return { id: item.id, order: index + 1 };
                      });
                      
                      console.log(newList);
                      saveDiningOptionOrder({
                        payload: newList,
                        subDomain: getSubdomain(),
                        stationId: selectedStation.id,
                      });
                    }}
                    header={
                      <div className={styles.header}>
                        <div className={styles.column1}></div>
                        <div style={{ width: "100%" }}>Title</div>
                        <div style={{ width: "160px", textAlign: "center", flexShrink: 0, }}>
                          Order
                        </div>
                        <div style={{ width: "260px", textAlign: "center", flexShrink: 0, }}>
                          Active
                        </div>
                        <div
                          style={{
                            width: "207px",
                            textAlign: "center",
                            flexShrink: 0,
                          }}
                        >
                          Actions
                        </div>
                      </div>
                    }
                    itemTemplate={itemTemplate}
                    dragdrop={!isSaveDiningOptionOrderLoading}
                    className={styles.orderListItem}
                  ></OrderList>
                </>
              )}
            </>
          ) : (
            <div>This Station don't have any dining options</div>
          )}
        </>
      ) : (
        <div>Please, select station</div>
      )}

      <Dialog
        header={`${mode === "create" ? "Create" : "Edit"} Dining Option`}
        visible={showModal}
        onHide={() => {
          setShowModal(false);
        }}
        footer={() => (
          <div className="flex gap-2 justify-content-end">
            <Button
              label="Cancel"
              className="p-button-text"
              onClick={() => {
                setShowModal(false);
              }}
            />
            <Button
              label="Save"
              type="submit"
              className="p-button-success"
              onClick={
                mode === "create"
                  ? handleSubmit(onCreate)
                  : handleSubmit(onSaveEdit)
              }
            />
          </div>
        )}
        className="p-fluid"
      >
        <form>
          <Controller
            name="title"
            control={control}
            render={({ field }) => (
              <div className="field">
                <label>Title</label>
                <InputText
                  className="w-full"
                  onKeyDown={handleKeyDown}
                  {...field}
                />
              </div>
            )}
          />

          <Controller
            name="active"
            control={control}
            render={({ field }) => (
              <SelectButton
                {...field}
                options={["Active", "Inactive"]}
                defaultValue={"Active"}
                allowEmpty={false}
              />
            )}
          />
        </form>
      </Dialog>
      
      <Dialog
        header={`${mode === "create" ? "New" : "Edit"} Dining Option`}
        visible={showModal}
        onHide={() => {
          setShowModal(false);
        }}
        footer={() => (
          <div className="flex gap-2 justify-content-end">
            <Button
              label="Save"
              type="submit"
              className={`p-button-success ${styles.saveBtn}`}
              onClick={
                mode === "create"
                  ? handleSubmit(onCreate)
                  : handleSubmit(onSaveEdit)
              }
            />
          </div>
        )}
        className={`p-fluid ${styles.diningOptionModal}`}
      >
        <form>
          <Controller
            name="title"
            control={control}
            render={({ field }) => (
              <div className="field">
                <label>Title</label>
                <InputText
                  className={`w-full mb-3 ${styles.input}`}
                  onKeyDown={handleKeyDown}
                  placeholder="Title"
                  {...field}
                />
              </div>
            )}
          />
          
            <Controller
              name="active"
              control={control}
              render={({ field }) => (
                <SelectButton
                  {...field}
                  options={["Active", "Inactive"]}
                  allowEmpty={false}
                  className={`${styles.selectButton}`}
                />
              )}
            />
        </form>
      </Dialog>
      
      {/* Delete modal */}
      <Dialog
        closable={false}
        header={
          <div style={{ width: '100%', textAlign: 'center' }}>
            Are You Sure?
          </div>}
        visible={showDeleteModal}
        onHide={() => {
          setShowDeleteModal(false);
        }}
        footer={() => (
          <div className="flex flex-column gap-2 justify-content-end">
            <Button
              label="Delete"
              className={`p-button-success ${styles.deleteDialogBtn}`}
              onClick={onDelete}
            />
            <Button
              label="Cancel"
              text
              className={`p-button-success ${styles.cancelDialogBtn}`}
              onClick={() => {setShowDeleteModal(false)}}
            />
          </div>
        )}
        className={`p-fluid ${styles.deleteModal}`}
      >
        <div className={styles.text}>
          <span>You want to delete the dining option <strong>{name}</strong>.</span>
          Confirm this action by pressing ‘Delete’ or cancel it.
        </div>
      </Dialog>
    </div>
  );
};

export default Scheduling;
